import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart
} from 'recharts';

// Colores para gráficos
const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
];

// Formato de moneda
const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

// Formato de números
const formatNumber = (value) => {
  return new Intl.NumberFormat('es-CO').format(value || 0);
};

// Helper: formatear correctamente fechas 'YYYY-MM-DD' como fecha local (evita desfase de zona horaria)
const formatYMDLabelES = (s) => {
  try {
    if (!s) return '';
    const str = String(s);
    // tomar solo la parte de fecha si viene con hora
    const ymd = str.includes('T') ? str.split('T')[0] : (str.includes(' ') ? str.split(' ')[0] : str);
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) {
      return new Date(str).toLocaleDateString('es-CO', { month: 'short', day: 'numeric' });
    }
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('es-CO', { month: 'short', day: 'numeric' });
  } catch {
    return String(s);
  }
};

// Componente: Gráfico de envíos diarios
export const DailyShipmentsChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando datos...</div>
      </div>
    );
  }

  if (!data?.chartData?.length) {
    return (
      <div className="h-80 flex items-center justify-center text-gray-500">
        No hay datos de envíos disponibles
      </div>
    );
  }

  return (
    <div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={[...data.chartData].reverse()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatYMDLabelES(value)}
            />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" tickFormatter={formatCurrency} />
            <Tooltip
              labelFormatter={(value) => formatYMDLabelES(value)}
              formatter={(value, name) => [
                (name === 'Ingresos' || name === 'revenue') ? formatCurrency(value) : formatNumber(value),
                name
              ]}
            />
            <Legend />
            <Bar yAxisId="left" dataKey="shipments" fill="#3B82F6" name="Envíos" />
            <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} name="Ingresos" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {data.summary && (
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{formatNumber(data.summary.totalShipments)}</div>
            <div className="text-gray-500">Total Envíos</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{formatNumber(data.summary.avgDailyShipments)}</div>
            <div className="text-gray-500">Promedio Diario</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{formatNumber(data.summary.lastWeekShipments)}</div>
            <div className="text-gray-500">Última Semana</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente: Gráfico de principales ciudades de envío
export const TopShippingCitiesChart = ({ data, loading }) => {
  // Procesar datos: Top 10 + Otros
  const processedData = React.useMemo(() => {
    if (!data?.length) return [];

    // 1. Clonar y ordenar por pedidos DESC
    const sorted = [...data].sort((a, b) => b.orderCount - a.orderCount);

    const totalOrders = data.reduce((sum, curr) => sum + curr.orderCount, 0);

    // 2. Tomar Top 10
    const top10 = sorted.slice(0, 10);

    // 3. Agrupar el resto
    const others = sorted.slice(10);
    let finalData = top10;

    if (others.length > 0) {
      const othersEntry = {
        city: 'Otras ciudades',
        orderCount: others.reduce((sum, curr) => sum + curr.orderCount, 0),
        totalRevenue: others.reduce((sum, curr) => sum + (curr.totalRevenue || 0), 0),
        isOthers: true
      };
      finalData = [...top10, othersEntry];
    }

    // Calcular porcentajes
    return finalData.map(item => ({
      ...item,
      percentage: totalOrders > 0 ? (item.orderCount / totalOrders) * 100 : 0
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando datos...</div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="h-96 flex items-center justify-center text-gray-500">
        No hay datos de ciudades disponibles
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
      {/* Chart Area */}
      <div className="h-64 mb-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={processedData} margin={{ top: 10, right: 40, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
            <XAxis
              dataKey="city"
              hide={true} // Hidden because it's in the table below
            />
            {/* Eje Izquierdo: Pedidos */}
            <YAxis
              yAxisId="left"
              tickFormatter={formatNumber}
              tick={{ fontSize: 9, fill: '#94A3B8' }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            {/* Eje Derecho: Porcentaje */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(val) => `${val}%`}
              tick={{ fontSize: 9, fill: '#F59E0B' }}
              domain={[0, 100]}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)', fontSize: '11px' }}
              formatter={(value, name) => {
                if (name === 'Pedidos') return [formatNumber(value), 'Pedidos'];
                if (name === 'Porcentaje') return [`${value.toFixed(1)}%`, 'Porcentaje'];
                return [value, name];
              }}
            />
            <Bar yAxisId="left" dataKey="orderCount" fill="#3B82F6" name="Pedidos" radius={[4, 4, 0, 0]} barSize={24}>
              {processedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.isOthers ? '#94A3B8' : COLORS[index % COLORS.length]} />
              ))}
            </Bar>
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="percentage"
              stroke="#F59E0B"
              name="Porcentaje"
              strokeWidth={2}
              dot={{ fill: '#F59E0B', r: 3 }}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Integrated Excel-style Table (Tabulación) */}
      <div className="w-full overflow-x-auto pb-2">
        <div style={{ minWidth: (processedData.length + 1) * 70, paddingLeft: '50px', paddingRight: '40px' }}>
          <table className="w-full border-collapse border border-gray-200">
            <tbody>
              {/* Row 1: City Names */}
              <tr className="bg-gray-50/50">
                <td className="border border-gray-200 px-2 py-1 text-[9px] font-black text-gray-500 uppercase bg-gray-100/80 sticky left-0 z-10 w-24">Ciudad</td>
                {processedData.map((item, idx) => (
                  <td key={idx} className="border border-gray-200 px-1 py-1 text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <div className="w-2 h-0.5 rounded-full" style={{ backgroundColor: item.isOthers ? '#94A3B8' : COLORS[idx % COLORS.length] }}></div>
                      <span className="text-[9px] font-bold text-gray-700 leading-tight uppercase truncate max-w-[60px]">{item.city}</span>
                    </div>
                  </td>
                ))}
              </tr>
              {/* Row 2: Counts */}
              <tr>
                <td className="border border-gray-200 px-2 py-1 text-[9px] font-black text-gray-500 uppercase bg-gray-100/80 sticky left-0 z-10">Pedidos</td>
                {processedData.map((item, idx) => (
                  <td key={idx} className="border border-gray-200 px-1 py-1 text-center text-[10px] font-black text-blue-600">
                    {formatNumber(item.orderCount)}
                  </td>
                ))}
              </tr>
              {/* Row 3: Percentages */}
              <tr>
                <td className="border border-gray-200 px-2 py-1 text-[9px] font-black text-gray-500 uppercase bg-gray-100/80 sticky left-0 z-10">% Total</td>
                {processedData.map((item, idx) => (
                  <td key={idx} className="border border-gray-200 px-1 py-1 text-center text-[10px] font-black text-amber-600">
                    {item.percentage.toFixed(0)}%
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Componente: Análisis de mejores clientes
export const TopCustomersTable = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando datos...</div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="h-80 flex items-center justify-center text-gray-500">
        No hay datos de clientes disponibles
      </div>
    );
  }

  return (
    <div className="overflow-auto max-h-[500px]">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Cliente
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Estado
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Pedidos
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total Ventas
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Ticket Prom.
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Actividad
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {data.slice(0, 15).map((customer, index) => {
            const initials = customer.name.slice(0, 2).toUpperCase();
            const colors = ['bg-blue-100 text-blue-600', 'bg-green-100 text-green-600', 'bg-purple-100 text-purple-600', 'bg-pink-100 text-pink-600', 'bg-indigo-100 text-indigo-600'];
            const avatarColor = colors[index % colors.length];

            return (
              <tr key={customer.document} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px] mr-2 ${avatarColor}`}>
                      {initials}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-800 line-clamp-1 max-w-[180px]" title={customer.name}>
                        {customer.name}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {customer.document}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${customer.customerType === 'VIP' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                    customer.customerType === 'Frecuente' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      'bg-gray-50 text-gray-600 border-gray-200'
                    }`}>
                    {customer.customerType === 'VIP' && <span className="mr-1">👑</span>}
                    {customer.customerType}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-700 font-medium">
                  {formatNumber(customer.orderCount)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-xs font-bold text-gray-900">
                  {formatCurrency(customer.totalSpent)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                  {formatCurrency(customer.avgOrderValue)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-400">
                  <div className="flex flex-col">
                    <span>{formatYMDLabelES(String(customer.lastOrderDate || '').slice(0, 10))}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Componente: Análisis de recompras de clientes
export const CustomerRepeatPurchasesChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando datos...</div>
      </div>
    );
  }

  if (!data?.distribution?.length) {
    return (
      <div className="h-80 flex items-center justify-center text-gray-500">
        No hay datos de recompras disponibles
      </div>
    );
  }

  return (
    <div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data.distribution}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ frequency, percentage }) => `${frequency}: ${percentage}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="customerCount"
            >
              {data.distribution.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [formatNumber(value), 'Clientes']} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {data.summary && (
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{formatNumber(data.summary.totalCustomers)}</div>
            <div className="text-gray-500">Total Clientes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{formatNumber(data.summary.repeatCustomers)}</div>
            <div className="text-gray-500">Clientes Repetitivos</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{data.summary.repeatRate}%</div>
            <div className="text-gray-500">Tasa de Recompra</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente: Nuevos clientes diarios
export const NewCustomersDailyChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando datos...</div>
      </div>
    );
  }

  if (!data?.chartData?.length) {
    return (
      <div className="h-80 flex items-center justify-center text-gray-500">
        No hay datos de nuevos clientes disponibles
      </div>
    );
  }

  return (
    <div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={[...data.chartData].reverse()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatYMDLabelES(value)}
            />
            <YAxis />
            <Tooltip
              labelFormatter={(value) => formatYMDLabelES(value)}
              formatter={(value) => [formatNumber(value), 'Nuevos Clientes']}
            />
            <Area
              type="monotone"
              dataKey="newCustomers"
              stroke="#10B981"
              fill="#10B981"
              fillOpacity={0.3}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {data.summary && (
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{formatNumber(data.summary.totalNewCustomers)}</div>
            <div className="text-gray-500">Total Nuevos</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{formatNumber(data.summary.avgDailyNew)}</div>
            <div className="text-gray-500">Promedio Diario</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{formatNumber(data.summary.lastWeekNew)}</div>
            <div className="text-gray-500">Última Semana</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente: Clientes perdidos
export const LostCustomersAnalysis = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando datos...</div>
      </div>
    );
  }

  if (!data?.byRiskLevel || Object.keys(data.byRiskLevel).length === 0) {
    return (
      <div className="h-80 flex items-center justify-center text-gray-500">
        No hay datos de clientes perdidos disponibles
      </div>
    );
  }

  const riskLevels = Object.entries(data.byRiskLevel).filter(([_, customers]) => customers.length > 0);

  return (
    <div>
      <div className="space-y-4">
        {riskLevels.map(([riskLevel, customers]) => (
          <div key={riskLevel}>
            <div className="flex items-center justify-between mb-2">
              <h4 className={`font-medium ${riskLevel === 'Muy perdido' ? 'text-red-600' :
                riskLevel === 'En riesgo alto' ? 'text-orange-600' :
                  riskLevel === 'En riesgo medio' ? 'text-yellow-600' :
                    'text-blue-600'
                }`}>
                {riskLevel} ({customers.length} clientes)
              </h4>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
              {customers.slice(0, 5).map((customer) => (
                <div key={customer.document} className="flex justify-between items-center py-1 text-sm">
                  <div>
                    <span className="font-medium">{customer.name}</span>
                    <span className="text-gray-500 ml-2">({customer.totalOrders} pedidos)</span>
                  </div>
                  <div className="text-right">
                    <div>{formatCurrency(customer.totalSpent)}</div>
                    <div className="text-xs text-gray-500">hace {customer.daysSinceLastOrder} días</div>
                  </div>
                </div>
              ))}
              {customers.length > 5 && (
                <div className="text-xs text-gray-500 mt-2">
                  ... y {customers.length - 5} clientes más
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {data.summary && (
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{formatNumber(data.summary.totalLostCustomers)}</div>
            <div className="text-gray-500">Clientes en Riesgo</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{formatCurrency(data.summary.potentialLostRevenue)}</div>
            <div className="text-gray-500">Ingresos en Riesgo</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente: Tendencias de ventas semanales
export const SalesTrendsChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando datos...</div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="h-80 flex items-center justify-center text-gray-500">
        No hay datos de tendencias disponibles
      </div>
    );
  }

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="weekStart"
            tickFormatter={(value) => formatYMDLabelES(value)}
          />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" tickFormatter={formatCurrency} />
          <Tooltip
            labelFormatter={(value) => `Semana del ${formatYMDLabelES(value)}`}
            formatter={(value, name) => [
              (name === 'Ingresos' || name === 'totalRevenue') ? formatCurrency(value) : formatNumber(value),
              name
            ]}
          />
          <Legend />
          <Bar yAxisId="left" dataKey="orderCount" fill="#3B82F6" name="Pedidos" />
          <Line yAxisId="right" type="monotone" dataKey="totalRevenue" stroke="#10B981" strokeWidth={2} name="Ingresos" />
          <Line yAxisId="left" type="monotone" dataKey="uniqueCustomers" stroke="#F59E0B" strokeWidth={2} name="Clientes Únicos" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// Componente: Rendimiento de productos
export const ProductPerformanceTable = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando datos...</div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="h-80 flex items-center justify-center text-gray-500">
        No hay datos de productos disponibles
      </div>
    );
  }

  return (
    <div className="overflow-auto max-h-[500px]">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Producto
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Pedidos
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Cantidad
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Ingresos
            </th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Precio Prom.
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {data.slice(0, 15).map((product, index) => (
            <tr key={product.productCode} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-2 whitespace-nowrap">
                <div className="text-xs font-bold text-gray-800 line-clamp-1 max-w-[200px]" title={product.productName}>
                  {product.productName}
                </div>
                <div className="text-[10px] text-gray-400">
                  {product.productCode}
                </div>
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-700 font-medium">
                {formatNumber(product.orderCount)}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">
                {formatNumber(product.totalQuantity)}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-xs font-bold text-gray-900">
                {formatCurrency(product.totalRevenue)}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                {formatCurrency(product.avgPrice)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
